import { useEffect, useRef } from 'react';

interface InkBrushProps {
  isDarkMode: boolean;
}

const InkBrush = ({ isDarkMode }: InkBrushProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    let animationId: number;
    let time = 0;

    const draw = () => {
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      time += 0.003;

      // Draw subtle ink brush strokes (zen calligraphy style)
      const strokes = [
        { x: width * 0.1, y: height * 0.2, length: width * 0.15, angle: -0.3 },
        { x: width * 0.85, y: height * 0.6, length: width * 0.12, angle: 0.5 },
        { x: width * 0.15, y: height * 0.75, length: width * 0.1, angle: -0.2 },
      ];

      strokes.forEach((stroke, i) => {
        // Subtle breathing animation
        const breathe = Math.sin(time + i * 0.5) * 0.15 + 0.85;
        const opacity = isDarkMode ? 0.08 : 0.05;

        // Draw brush stroke with gradient (wider at start, tapered at end)
        const gradient = ctx.createLinearGradient(
          stroke.x,
          stroke.y,
          stroke.x + Math.cos(stroke.angle) * stroke.length,
          stroke.y + Math.sin(stroke.angle) * stroke.length
        );

        const color = isDarkMode ? '220, 180, 190' : '60, 30, 40';
        gradient.addColorStop(0, `rgba(${color}, ${opacity * breathe})`);
        gradient.addColorStop(0.5, `rgba(${color}, ${opacity * 0.8 * breathe})`);
        gradient.addColorStop(1, `rgba(${color}, 0)`);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 20 * breathe;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(stroke.x, stroke.y);
        ctx.lineTo(
          stroke.x + Math.cos(stroke.angle) * stroke.length,
          stroke.y + Math.sin(stroke.angle) * stroke.length
        );
        ctx.stroke();
      });

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        opacity: 0.4,
      }}
    />
  );
};

export default InkBrush;
